import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import ReportView from '../components/report/ReportView';
import { Post } from '../models/post';
import { ChildProfile } from '../models/childProfile';
import { PostDataService } from '../services/postDataService';
import { ChildProfileDataService } from '../services/childProfileDataService';
import { ReportTemplateDataService } from '../services/reportTemplateDataService';
import { formatDateFullWithOrdinal } from '../services/sharedService';
import { useAppState } from '../context/AppStateContext';
import { ReportTemplate, STOCK_WEEKLY_TEMPLATE, templateNeedsAttendance } from '../models/reportTemplate';
import { AttendanceStats, fetchAttendanceStats } from '../services/attendanceStatsService';
import { ORG_CONFIG_DEFAULTS } from '../models/orgConfig';
import apiInstance from '../api/axios';

// Rendered by Puppeteer (server-side) or opened directly in the browser.
// Example: http://localhost:3001/pdf-report?childId=11&startDate=2025-10-20&reportDate=2025-10-24
// When invoked by Puppeteer a `token` query param carries the JWT for API authentication.

const PdfReportRoute: React.FC = () => {
  const [searchParams] = useSearchParams();
  const childId = searchParams.get('childId') ?? '';
  const reportDate = searchParams.get('reportDate') ?? undefined;
  const startDate = searchParams.get('startDate') ?? undefined;
  const endDate = searchParams.get('endDate') ?? undefined;
  const showHeaderParam = searchParams.get('showHeader');
  const includeHeader = showHeaderParam === 'false' || showHeaderParam === '0' ? false : true;
  // JWT forwarded by the API when Puppeteer renders this page
  const token = searchParams.get('token') ?? undefined;
  // Org id forwarded by the API — sent as X-Organization-Id so the backend
  // scopes data to the right tenant. The raw axios calls below bypass the app's
  // interceptor (which normally injects this from localStorage), so without it
  // the API falls back to DEFAULT_ORG_ID and 403/404s on a non-default org.
  const organisationId = searchParams.get('organisationId') ?? undefined;
  // Report author name forwarded by the API (the PIN-active user who triggered generation)
  const authorName = searchParams.get('authorName') ?? undefined;
  // Report Builder template driving the header: 'stock' = the code-defined
  // stock Weekly Report, a number = a data.report_template row (fetched below).
  // Absent (hand-typed URL or a pre-template API) also falls back to the
  // stock minimal header via ReportView's default.
  const templateIdParam = searchParams.get('templateId');
  // Generation-panel values for manual-editable header fields, JSON-encoded.
  const fieldValuesParam = searchParams.get('fieldValues');

  const { selectedOrganisation, orgConfig } = useAppState();

  const [posts, setPosts] = useState<Post[]>([]);
  const [childName, setChildName] = useState<string>('');
  const [child, setChild] = useState<ChildProfile | null>(null);
  // null = no templateId param → ReportView falls back to the stock header.
  const [template, setTemplate] = useState<ReportTemplate | null>(null);
  const [attendance, setAttendance] = useState<AttendanceStats | null>(null);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [brandColour, setBrandColour] = useState<string | null>(null);
  // `orgName` here is the *report-header* label, NOT the legal org name —
  // prefers data.organisation_config.setting_name when present, falls back
  // to security.organisation.name. Both surfaces live behind the same
  // local state so the Puppeteer + normal-browser branches don't diverge.
  const [orgName, setOrgName] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      if (!childId) {
        setError('Missing required query parameter: childId');
        setLoading(false);
        return;
      }

      // Turn an axios failure into a message that names the call and surfaces
      // the HTTP status + server message. Without this a 403/404 just bubbles
      // up as a generic "Request failed", and the Puppeteer worker only sees a
      // selector timeout — the real cause (wrong org, missing child) is lost.
      const describeFetchError = (label: string, err: any): string => {
        const status = err?.response?.status;
        const serverMsg = err?.response?.data?.error || err?.response?.data?.message;
        if (status) return `${label} failed (HTTP ${status}${serverMsg ? `: ${serverMsg}` : ''})`;
        return `${label} failed: ${err?.message ?? 'network error'}`;
      };

      try {
        let fetchedPosts: Post[];
        let fetchedChild: ChildProfile | null = null;
        let fetchedTemplate: ReportTemplate | null =
          templateIdParam === 'stock' ? STOCK_WEEKLY_TEMPLATE : null;

        // How to load attendance stats — and the grace window — differs per
        // path: the Puppeteer render uses a JWT-bearing raw instance, the
        // browser uses the interceptor-backed app instance. Captured here,
        // populated per branch, consumed once below.
        let attendanceHttp = apiInstance;
        let grace = orgConfig?.attendanceGraceMinutes ?? ORG_CONFIG_DEFAULTS.attendanceGraceMinutes;

        if (token) {
          // Puppeteer path: no Amplify session – use the forwarded JWT directly
          const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000/api';
          const headers: Record<string, string> = { Authorization: `Bearer ${token}` };
          if (organisationId) headers['X-Organization-Id'] = organisationId;
          // A raw instance carrying the forwarded JWT + org header, mirroring
          // the per-request headers used for posts/child above, so the shared
          // attendance fetch works without the app's axios interceptor.
          attendanceHttp = axios.create({ baseURL: baseUrl, headers });

          let postsUrl = `${baseUrl}/posts?childId=${childId}`;
          if (startDate) postsUrl += `&fromDate=${encodeURIComponent(startDate)}`;
          let postsResponse;
          try {
            postsResponse = await axios.get(postsUrl, { headers });
          } catch (err: any) {
            throw new Error(describeFetchError('Loading posts', err));
          }
          fetchedPosts = postsResponse.data;
          if (!Array.isArray(fetchedPosts)) {
            throw new Error(`Loading posts returned an unexpected response shape (expected an array, got ${typeof fetchedPosts})`);
          }

          try {
            const childResponse = await axios.get(`${baseUrl}/child_profiles/${childId}`, { headers });
            fetchedChild = childResponse.data;
          } catch (err: any) {
            throw new Error(describeFetchError('Loading child profile', err));
          }

          if (templateIdParam && templateIdParam !== 'stock') {
            try {
              const templateResponse = await axios.get(`${baseUrl}/report-templates/${templateIdParam}`, { headers });
              fetchedTemplate = templateResponse.data;
            } catch {
              // Template deleted between the generate request and this render —
              // mirror the API's fallback to the stock template.
              fetchedTemplate = STOCK_WEEKLY_TEMPLATE;
            }
          }

          // Fetch org logo + name for Puppeteer path (no AppStateContext session).
          // Also fetch the per-org config so the report header can prefer
          // setting_name over the legal org name when configured.
          try {
            const orgResponse = await axios.get(`${baseUrl}/security/organisation`, { headers });
            setLogoUrl(orgResponse.data.logoUrl || null);
            setBrandColour(orgResponse.data.brandColour || null);
            let header: string | null = orgResponse.data.name || null;
            try {
              const cfgResponse = await axios.get(`${baseUrl}/security/organisation/config`, { headers });
              if (cfgResponse.data?.settingName) header = cfgResponse.data.settingName;
              if (typeof cfgResponse.data?.attendanceGraceMinutes === 'number') {
                grace = cfgResponse.data.attendanceGraceMinutes;
              }
            } catch { /* config endpoint unavailable — header stays as org name */ }
            setOrgName(header);
          } catch { /* org info unavailable — fall back to defaults */ }
        } else {
          // Normal browser path: Amplify session provides the JWT via the axios interceptor
          fetchedPosts = await PostDataService.getPosts(childId, startDate ?? null);
          const profiles = await ChildProfileDataService.getChildProfiles();
          fetchedChild = profiles.find(p => String(p.id) === childId) ?? null;
          // Logo comes from AppStateContext (selectedOrganisation.logoUrl) — set in render below

          if (templateIdParam && templateIdParam !== 'stock') {
            try {
              fetchedTemplate = await ReportTemplateDataService.getTemplate(parseInt(templateIdParam, 10));
            } catch {
              fetchedTemplate = STOCK_WEEKLY_TEMPLATE;
            }
          }
        }

        let filtered = fetchedPosts.filter((p) => p.is_published);
        if (endDate) {
          const end = new Date(endDate);
          end.setHours(23, 59, 59, 999);
          filtered = filtered.filter((p) => new Date(p.post_date) <= end);
        }

        setPosts(filtered);
        setChild(fetchedChild);
        setTemplate(fetchedTemplate);
        if (fetchedChild) {
          setChildName(`${fetchedChild.first_name} ${fetchedChild.last_name}`);
        }

        // Attendance stats (Phase 2) — only when the template references
        // attendance. Awaited so the chart SVGs are in the DOM before
        // Puppeteer snapshots the page (the .images-loaded gate covers imgs,
        // not charts, so the data must be present at first paint).
        const effectiveTemplate = fetchedTemplate ?? STOCK_WEEKLY_TEMPLATE;
        if (startDate && endDate && templateNeedsAttendance(effectiveTemplate.config)) {
          const stats = await fetchAttendanceStats(attendanceHttp, parseInt(childId), startDate, endDate, grace);
          setAttendance(stats);
        }
      } catch (err: any) {
        console.error('Failed to load posts for pdf-report route', err);
        setError(err?.message ?? 'Failed to load posts');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [childId, startDate, endDate, token, organisationId, templateIdParam]);

  if (loading) return <div>Loading report...</div>;
  if (error) return <div style={{ padding: 20 }}>Error rendering report: {error}</div>;

  // Manual-field values entered on the generation panel, forwarded by the API.
  let fieldValues: Record<string, string> | undefined;
  if (fieldValuesParam) {
    try {
      fieldValues = JSON.parse(fieldValuesParam);
    } catch {
      console.warn('Ignoring malformed fieldValues query param');
    }
  }

  return (
    <div style={{ backgroundColor: 'white', padding: '20px', minHeight: '100vh' }}>
      <ReportView
        posts={posts}
        childName={childName}
        child={child}
        reportDate={formatDateFullWithOrdinal(reportDate ?? new Date().toISOString(), false)}
        startDate={startDate}
        endDate={endDate}
        includeHeader={includeHeader}
        useThumbnails={false}
        logoUrl={logoUrl || selectedOrganisation?.logoUrl}
        orgName={orgName ?? orgConfig?.settingName ?? selectedOrganisation?.name}
        authorName={authorName}
        brandColour={brandColour || selectedOrganisation?.brandColour}
        // No templateId param → undefined → ReportView's stock default header
        template={template?.config}
        templateName={template?.name}
        fieldValues={fieldValues}
        attendance={attendance}
      />
    </div>
  );
};

export default PdfReportRoute;
